import Inspections from 'assets/inspections-tab-icon.svg?react'
import { PARTS_DATABASE_PERMISSIONS_LIST } from 'features/partsDatabase'
import {
  AlertTriangle,
  Bell,
  BellRing,
  Blocks,
  CalendarRange,
  Camera,
  ChartGantt,
  ChartLine,
  ChartPie,
  ClipboardCheck,
  ClipboardList,
  Database,
  DollarSign,
  FileText,
  GitBranch,
  Heart,
  Home,
  Layers,
  Map,
  Network,
  Notebook,
  NotebookPen,
  PencilLine,
  Ship,
  Workflow,
} from 'lucide-react'
import React from 'react'
import {
  SEARCH_PARAM_ORG_ID,
  SEARCH_PARAM_SITE_ID,
} from 'routes/(global_search_params)/-searchParams'
import { createLibraSearch } from 'routes/libra/-searchParams'
import { createPointCloudIndexSearch } from 'routes/point-cloud/-searchParams'
import { createProcessDataOrgSiteSearch } from 'routes/process-data/-searchParams'
import { createProcessFlowsSearch } from 'routes/process-flows/-searchParams'
import { createProductionDashboardSearch } from 'routes/production-dashboard/-searchParams'
import { createShipsSearch } from 'routes/ships/-searchParams'
import { createVirtualReportsSearch } from 'routes/virtual-reports/-searchParams'
import { createWalkdownsSearch } from 'routes/walkdowns/-searchParams'
import { createWalkthroughSearch } from 'routes/walkthrough/-searchParams'
import type { FileRouteTypes } from 'routeTree.gen'
import styled, { css } from 'styled-components'
import {
  getOrgEditPermission,
  PERMISSION_CANTILEVER_ANOMALY_DETECTION_EDITOR,
  PERMISSION_CANTILEVER_BUDGETING_EDITOR,
  PERMISSION_CANTILEVER_BUDGETING_VIEWER,
  PERMISSION_CANTILEVER_CASES_EDITOR,
  PERMISSION_CANTILEVER_CASES_VIEWER,
  PERMISSION_CANTILEVER_HEALTH_PLAN_EDITOR,
  PERMISSION_CANTILEVER_HEALTH_PLAN_VIEWER,
  PERMISSION_CANTILEVER_INSPECTION_OVERVIEW_ADMIN,
  PERMISSION_CANTILEVER_INSPECTION_OVERVIEW_EDITOR,
  PERMISSION_CANTILEVER_INSPECTION_SCHEDULING_EDITOR,
  PERMISSION_CANTILEVER_INSPECTION_SCHEDULING_VIEWER,
  PERMISSION_CANTILEVER_POWER_DOCUMENT_NAVIGATOR_ADMIN,
  PERMISSION_CANTILEVER_POWER_DOCUMENT_NAVIGATOR_EDITOR,
  PERMISSION_CANTILEVER_POWER_DOCUMENT_NAVIGATOR_VIEWER,
  PERMISSION_CANTILEVER_PROCESS_FLOW_ADMIN,
  PERMISSION_CANTILEVER_SYSTEM_VIEWER,
  PERMISSION_CANTILEVER_TIME_SERIES_EDITOR,
  PERMISSION_GECKO_INTERNAL_MEMBER,
  PERMISSION_MATTERPORT_WALKTHROUGH_VIEWER,
  RolodexPermissionsValues,
} from 'types'

import { SelectedTab } from './types'

const FilledIcon = css`
  height: 1.25rem;
  width: 1.25rem;

  path {
    fill: currentColor;
  }
`

const OutlinedIcon = css`
  height: 1.25rem;
  width: 1.25rem;

  path {
    background-color: ${({ theme }) => theme.forecast.tertiaryText};
  }

  &:active {
    path {
      background-color: ${({ theme }) => theme.forecast.secondaryText};
    }
  }
`

const InspectionsIcon = styled(Inspections)`
  ${FilledIcon}
`

const HeartIcon = styled(Heart)`
  ${OutlinedIcon}
`

const HomeIcon = styled(Home)`
  ${OutlinedIcon}
`

const MapIcon = styled(Map)`
  ${OutlinedIcon}
`

const PencilIcon = styled(PencilLine)`
  ${OutlinedIcon}
`

const CalendarIcon = styled(CalendarRange)`
  ${FilledIcon}
`

const ChartLineIcon = styled(ChartLine)`
  ${OutlinedIcon}
`

const ClipboardIcon = styled(ClipboardList)`
  ${OutlinedIcon}
`

const ClaimsIcon = styled(ClipboardCheck)`
  ${OutlinedIcon}
`

const PieChartIcon = styled(ChartPie)`
  ${OutlinedIcon}
`

const BarChartIcon = styled(ChartGantt)`
  ${OutlinedIcon}
`

const DashboardIcon = styled(ChartGantt)`
  ${OutlinedIcon}
`

const PartsIcon = styled(Blocks)`
  ${OutlinedIcon}
`

const NetworkIcon = styled(Network)`
  ${OutlinedIcon}
`

const ProcessFlowIcon = styled(GitBranch)`
  ${OutlinedIcon}
`

const AssetsIcon = styled(Layers)`
  ${OutlinedIcon}
`

const DocIcon = styled(FileText)`
  ${OutlinedIcon}
`

const CasesIcon = styled(Notebook)`
  ${OutlinedIcon}
`

const LogbookIcon = styled(NotebookPen)`
  ${OutlinedIcon}
`

const ReportingIcon = styled(FileText)`
  ${OutlinedIcon}
`

const BudgetingIcon = styled(DollarSign)`
  ${OutlinedIcon}
`

const ProcessDataIcon = styled(ChartLine)`
  ${OutlinedIcon}
`
const MatterportWalkthroughIcon = styled(Camera)`
  ${OutlinedIcon}
`
const ShipsIcon = styled(Ship)`
  ${OutlinedIcon}
`

const AnomalyDetectionIcon = styled(AlertTriangle)`
  ${OutlinedIcon}
`

const AlertsIcon = styled(Bell)`
  ${OutlinedIcon}
`

const NotificationsIcon = styled(BellRing)`
  ${OutlinedIcon}
`

const WorkflowIcon = styled(Workflow)`
  ${OutlinedIcon}
`

const DatabaseIcon = styled(Database)`
  ${OutlinedIcon}
`

const US_NAVY_ORG_PERMISSION = getOrgEditPermission('us-navy-69a72a')

const LONGRIDGE_SITE_ID = 'longridge-ac0a01'

export interface NavItemConfig {
  to: FileRouteTypes['to']
  params?: (siteID: string, orgID?: string) => Record<string, string>
  search?: (
    siteID: string,
    orgID: string | undefined
  ) => Record<string, string | undefined>
  icon: React.ComponentType
  label: string
  filledIcon?: boolean
  permissions?: RolodexPermissionsValues[]
  except?: RolodexPermissionsValues[]
  shouldShow?: (context: {
    siteID: string
    canVisitSiteView: boolean
  }) => boolean
}

export const NAV_ITEMS_CONFIG: Partial<Record<SelectedTab, NavItemConfig>> = {
  Home: {
    to: '/home',
    icon: HomeIcon,
    label: 'Home',
    filledIcon: false,
  },
  Overview: {
    to: '/overview',
    search: (siteID, orgID) => ({
      [SEARCH_PARAM_ORG_ID]: orgID,
      [SEARCH_PARAM_SITE_ID]: siteID || undefined,
    }),
    icon: MapIcon,
    label: 'Site Overview',
    filledIcon: false,
    shouldShow: ({ canVisitSiteView }) => canVisitSiteView,
  },
  'Production Dashboard': {
    to: '/production-dashboard',
    search: (siteID, orgID) =>
      createProductionDashboardSearch({
        orgId: orgID ?? '',
        siteId: siteID ?? '',
      }),
    icon: DashboardIcon,
    label: 'Production Dashboard',
    filledIcon: false,
  },
  Inbox: {
    to: '/inspection-dashboard/$orgOrSiteID',
    params: (siteID, orgID) => ({ orgOrSiteID: siteID ?? orgID ?? '' }),
    icon: ClipboardIcon,
    label: 'Inspection Dashboard',
    filledIcon: false,
  },
  Documents: {
    to: '/documents',
    params: (siteID) => ({ siteID }),
    icon: DocIcon,
    label: 'Documents',
    filledIcon: false,
    permissions: [
      PERMISSION_CANTILEVER_POWER_DOCUMENT_NAVIGATOR_ADMIN,
      PERMISSION_CANTILEVER_POWER_DOCUMENT_NAVIGATOR_VIEWER,
      PERMISSION_CANTILEVER_POWER_DOCUMENT_NAVIGATOR_EDITOR,
    ],
  },
  Cases: {
    to: '/cases',
    icon: CasesIcon,
    label: 'Cases',
    filledIcon: false,
    permissions: [
      PERMISSION_CANTILEVER_CASES_EDITOR,
      PERMISSION_CANTILEVER_CASES_VIEWER,
      PERMISSION_GECKO_INTERNAL_MEMBER,
    ],
  },
  Alerts: {
    to: '/alerts',
    icon: AlertsIcon,
    label: 'Alerts',
    filledIcon: false,
    // TODO: Re-enable permissions for alerts viewing after dev
    // permissions: [
    //   PERMISSION_CANTILEVER_ALERTS_VIEWER,
    //   PERMISSION_CANTILEVER_ALERTS_EDITOR,
    // ],
  },
  Notifications: {
    to: '/notifications',
    icon: NotificationsIcon,
    label: 'Notifications',
    filledIcon: false,
  },
  Assets: {
    to: '/assets/{-$siteID}',
    params: (siteID) => ({ siteID }),
    icon: AssetsIcon,
    label: 'Assets',
  },
  Ships: {
    to: '/ships',
    search: (siteID, orgID) =>
      createShipsSearch({
        orgId: orgID,
        siteId: siteID || undefined,
      }),
    icon: ShipsIcon,
    label: 'Ships',
    filledIcon: false,
    permissions: [PERMISSION_GECKO_INTERNAL_MEMBER],
  },
  Inspections: {
    to: '/inspections',
    search: (siteID) => ({
      [SEARCH_PARAM_SITE_ID]: siteID || undefined,
    }),
    icon: InspectionsIcon,
    label: 'Inspections',
  },
  'Entry Browser': {
    to: '/entry-browser',
    icon: DatabaseIcon,
    label: 'Entry Browser',
    filledIcon: false,
    permissions: [PERMISSION_GECKO_INTERNAL_MEMBER],
  },
  Pipelines: {
    to: '/pipelines',
    icon: WorkflowIcon,
    label: 'Pipelines',
    filledIcon: false,
    permissions: [PERMISSION_GECKO_INTERNAL_MEMBER],
  },
  'Fleet Analysis': {
    to: '/fleet-analysis',
    search: (siteID, orgID) => ({
      [SEARCH_PARAM_ORG_ID]: orgID,
      [SEARCH_PARAM_SITE_ID]: siteID || undefined,
    }),
    icon: ChartLineIcon,
    label: 'Fleet Analysis',
    filledIcon: false,
    permissions: [PERMISSION_GECKO_INTERNAL_MEMBER],
  },
  'Inspection Scheduling': {
    to: '/scheduling',
    search: (siteID, orgID) => ({
      [SEARCH_PARAM_ORG_ID]: orgID,
      [SEARCH_PARAM_SITE_ID]: siteID || undefined,
    }),
    icon: CalendarIcon,
    label: 'Inspection Scheduling',
    permissions: [
      PERMISSION_CANTILEVER_INSPECTION_SCHEDULING_EDITOR,
      PERMISSION_CANTILEVER_INSPECTION_SCHEDULING_VIEWER,
      PERMISSION_GECKO_INTERNAL_MEMBER,
    ],
    except: [US_NAVY_ORG_PERMISSION],
  },
  'Availability Planning': {
    to: '/availability-planning',
    icon: CalendarIcon,
    label: 'Availability Planning',
    permissions: [US_NAVY_ORG_PERMISSION, PERMISSION_GECKO_INTERNAL_MEMBER],
  },
  'Health Plans': {
    to: '/health-plans',
    search: (siteID, orgID) => ({
      [SEARCH_PARAM_ORG_ID]: orgID,
      [SEARCH_PARAM_SITE_ID]: siteID || undefined,
    }),
    icon: HeartIcon,
    label: 'Health Plans',
    filledIcon: false,
    permissions: [
      PERMISSION_CANTILEVER_HEALTH_PLAN_VIEWER,
      PERMISSION_CANTILEVER_HEALTH_PLAN_EDITOR,
      PERMISSION_GECKO_INTERNAL_MEMBER,
    ],
    except: [US_NAVY_ORG_PERMISSION],
  },
  Budgeting: {
    to: '/budgeting',
    icon: BudgetingIcon,
    label: 'Budgeting',
    filledIcon: false,
    permissions: [
      PERMISSION_CANTILEVER_BUDGETING_EDITOR,
      PERMISSION_CANTILEVER_BUDGETING_VIEWER,
      PERMISSION_GECKO_INTERNAL_MEMBER,
    ],
  },
  Walkdowns: {
    to: '/walkdowns',
    search: (siteId) => createWalkdownsSearch(siteId),
    icon: ClipboardIcon,
    label: 'Walkdowns',
    filledIcon: false,
    permissions: [PERMISSION_GECKO_INTERNAL_MEMBER],
    except: [US_NAVY_ORG_PERMISSION],
  },
  Reporting: {
    to: '/reporting',
    icon: ReportingIcon,
    label: 'Reporting',
    filledIcon: false,
    permissions: [PERMISSION_GECKO_INTERNAL_MEMBER],
  },
  'System Lens': {
    to: '/system-lens',
    search: (siteId, orgId) => ({
      [SEARCH_PARAM_ORG_ID]: orgId,
      [SEARCH_PARAM_SITE_ID]: siteId,
    }),
    icon: NetworkIcon,
    label: 'System Lens',
    filledIcon: false,
    permissions: [
      PERMISSION_CANTILEVER_SYSTEM_VIEWER,
      PERMISSION_GECKO_INTERNAL_MEMBER,
    ],
  },
  'Point Cloud': {
    to: '/point-cloud',
    search: (siteId, orgId) =>
      createPointCloudIndexSearch({
        orgId: orgId,
        siteId: siteId,
      }),
    icon: MapIcon,
    label: '3D View',
    filledIcon: false,
    permissions: [
      PERMISSION_CANTILEVER_SYSTEM_VIEWER,
      PERMISSION_GECKO_INTERNAL_MEMBER,
    ],
    except: [US_NAVY_ORG_PERMISSION],
  },
  'Weld Map': {
    to: '/weldmap',
    search: (siteID, orgID) => ({
      [SEARCH_PARAM_ORG_ID]: orgID,
      [SEARCH_PARAM_SITE_ID]: siteID || undefined,
    }),
    icon: PencilIcon,
    label: 'Weld Map',
    filledIcon: false,
  },
  Walkthrough: {
    to: '/walkthrough',
    search: (siteId, orgId) =>
      createWalkthroughSearch({
        orgId,
        siteId: siteId || undefined,
      }),
    icon: MatterportWalkthroughIcon,
    label: 'Walkthrough',
    filledIcon: false,
    permissions: [
      PERMISSION_MATTERPORT_WALKTHROUGH_VIEWER,
      PERMISSION_GECKO_INTERNAL_MEMBER,
    ],
    except: [US_NAVY_ORG_PERMISSION],
  },
  Explorer: {
    to: '/explorer/$orgOrSiteID',
    params: (siteID, orgID) => ({ orgOrSiteID: siteID ?? orgID ?? '' }),
    icon: PieChartIcon,
    label: 'Explorer',
    filledIcon: false,
  },
  'Parts Thread': {
    to: '/parts',
    search: (siteID, orgID) => ({
      [SEARCH_PARAM_ORG_ID]: orgID,
      [SEARCH_PARAM_SITE_ID]: siteID || undefined,
    }),
    icon: PartsIcon,
    label: 'Parts Thread',
    filledIcon: false,
    permissions: PARTS_DATABASE_PERMISSIONS_LIST,
    except: [US_NAVY_ORG_PERMISSION],
  },
  'Process Flow Diagrams': {
    to: '/process-flows',
    search: (siteID, orgID) => {
      if (!siteID) {
        return {
          [SEARCH_PARAM_ORG_ID]: orgID,
          [SEARCH_PARAM_SITE_ID]: undefined,
        }
      }

      return createProcessFlowsSearch({
        orgId: orgID,
        siteId: siteID,
      })
    },
    icon: ProcessFlowIcon,
    label: 'Process Flow Diagrams',
    filledIcon: false,
    permissions: [PERMISSION_CANTILEVER_PROCESS_FLOW_ADMIN],
  },
  'Value Stories': {
    to: '/value-stories',
    search: (siteID, orgID) => ({
      [SEARCH_PARAM_ORG_ID]: orgID,
      [SEARCH_PARAM_SITE_ID]: siteID,
    }),
    icon: DocIcon,
    label: 'Value Stories',
    filledIcon: false,
    permissions: [PERMISSION_GECKO_INTERNAL_MEMBER],
  },
  'Jobs Overview': {
    to: '/jobs',
    icon: BarChartIcon,
    label: 'Jobs Overview',
    filledIcon: false,
    permissions: [
      PERMISSION_CANTILEVER_INSPECTION_OVERVIEW_ADMIN,
      PERMISSION_CANTILEVER_INSPECTION_OVERVIEW_EDITOR,
    ],
    except: [US_NAVY_ORG_PERMISSION],
  },
  'Process Data': {
    to: '/process-data',
    search: (siteId, orgId) =>
      createProcessDataOrgSiteSearch({
        orgId: orgId,
        siteId: siteId,
      }),
    icon: ProcessDataIcon,
    label: 'Process Data',
    filledIcon: false,
    permissions: [PERMISSION_CANTILEVER_TIME_SERIES_EDITOR],
  },
  'Anomaly Detection': {
    to: '/anomaly-detection',
    icon: AnomalyDetectionIcon,
    label: 'Anomaly Detection',
    filledIcon: false,
    permissions: [PERMISSION_CANTILEVER_ANOMALY_DETECTION_EDITOR],
  },
  Libra: {
    to: '/libra',
    search: (_siteID, orgID) =>
      createLibraSearch({
        orgId: orgID,
        workflowId: undefined,
        ticket: undefined,
        thread: undefined,
      }),
    icon: WorkflowIcon,
    label: 'Libra',
    filledIcon: false,
  },
  'NII Dashboard': {
    to: '/nii-dashboard',
    icon: DashboardIcon,
    label: 'NII Dashboard',
    filledIcon: false,
  },
  'Virtual Reports': {
    to: '/virtual-reports',
    search: (siteId, orgId) =>
      createVirtualReportsSearch({
        orgId,
        siteId: siteId || undefined,
      }),
    icon: DocIcon,
    label: 'Virtual Reports',
    filledIcon: false,
  },
  'Site Context & Monitoring': {
    to: '/site-context-monitoring',
    search: (siteID) => ({
      [SEARCH_PARAM_SITE_ID]: siteID || undefined,
    }),
    icon: ProcessFlowIcon,
    label: 'Site Context & Monitoring',
    permissions: [PERMISSION_GECKO_INTERNAL_MEMBER],
    shouldShow: ({ siteID }) => siteID === LONGRIDGE_SITE_ID,
  },
  Claims: {
    to: '/dossiers',
    search: (siteID) => ({
      [SEARCH_PARAM_SITE_ID]: siteID || undefined,
    }),
    icon: ClaimsIcon,
    label: 'Dossiers',
    permissions: [PERMISSION_GECKO_INTERNAL_MEMBER],
    shouldShow: ({ siteID }) => siteID === LONGRIDGE_SITE_ID,
  },
  Logbook: {
    to: '/logbook',
    search: (siteID) => ({
      [SEARCH_PARAM_SITE_ID]: siteID || undefined,
    }),
    icon: LogbookIcon,
    label: 'Logbook',
    filledIcon: false,
    permissions: [PERMISSION_GECKO_INTERNAL_MEMBER],
  },
}
